import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Navbar from '../../components/layout/Navbar';
import Input from '../../components/common/Input';
import Button from '../../components/common/Button';
import { useAuth } from '../../context/AuthContext';
import { useToast } from '../../context/ToastContext';
import { useTheme } from '../../context/ThemeContext';
import authService from '../../api/services/authService';
import { handleApiError } from '../../utils/helpers';
import { validateEmail, validatePhone, validatePassword } from '../../utils/validators';

const RegisterPage = () => {
  const navigate = useNavigate();
  const { login } = useAuth();
  const { success, error, info } = useToast();
  const { dark } = useTheme();
  const d = dark;

  const [formData, setFormData] = useState({
    email: '',
    password: '',
    confirmPassword: '',
    firstName: '',
    lastName: '',
    phone: '',
    idNumber: '',
    hostName: '',
    hostEmail: '',
    hostPhone: '',
    hostDescription: ''
  });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name]) setErrors(prev => ({ ...prev, [name]: '' }));
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.firstName) newErrors.firstName = 'First name is required';
    if (!formData.lastName) newErrors.lastName = 'Last name is required';
    if (!formData.email) { newErrors.email = 'Email is required'; }
    else if (!validateEmail(formData.email)) { newErrors.email = 'Invalid email format'; }
    if (!formData.phone) { newErrors.phone = 'Phone number is required'; }
    else if (!validatePhone(formData.phone)) { newErrors.phone = 'Invalid phone format (e.g., +233241234567)'; }
    if (!formData.idNumber) newErrors.idNumber = 'ID number is required';
    if (!formData.password) { newErrors.password = 'Password is required'; }
    else {
      const passwordValidation = validatePassword(formData.password);
      if (!passwordValidation.isValid) {
        newErrors.password = 'Password must be at least 8 characters with uppercase, lowercase, and number';
      }
    }
    if (formData.password !== formData.confirmPassword) newErrors.confirmPassword = 'Passwords do not match';
    if (!formData.hostName) newErrors.hostName = 'Organization name is required';
    if (!formData.hostEmail) { newErrors.hostEmail = 'Organization email is required'; }
    else if (!validateEmail(formData.hostEmail)) { newErrors.hostEmail = 'Invalid email format'; }
    if (!formData.hostPhone) { newErrors.hostPhone = 'Organization phone is required'; }
    else if (!validatePhone(formData.hostPhone)) { newErrors.hostPhone = 'Invalid phone format'; }
    if (!formData.hostDescription) newErrors.hostDescription = 'Organization description is required';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;
    setLoading(true);
    try {
      const response = await authService.register(formData);
      if (response.success) {
        login(response.data.user, response.data.token);
        success('Registration successful!');
        info('Your account is pending approval. You will be notified once approved.');
        navigate('/pending-approval');
      }
    } catch (err) {
      error(handleApiError(err));
    } finally {
      setLoading(false);
    }
  };

  // Password strength
  const pwLen = formData.password.length >= 8;
  const pwCase = /[A-Z]/.test(formData.password) && /[a-z]/.test(formData.password);
  const pwNum = /[0-9]/.test(formData.password);
  const strength = [pwLen, pwCase, pwNum].filter(Boolean).length;
  const strengthColor = strength === 3 ? '#22c55e' : strength === 2 ? '#f97316' : '#ef4444';
  const strengthLabel = strength === 3 ? 'Strong' : strength === 2 ? 'Medium' : 'Weak';

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;500;600;700;800;900&display=swap');
        .rp * { font-family: 'Plus Jakarta Sans', sans-serif !important; }

        @keyframes rp-up    { from{opacity:0;transform:translateY(24px)} to{opacity:1;transform:translateY(0)} }
        @keyframes rp-left  { from{opacity:0;transform:translateX(-24px)} to{opacity:1;transform:translateX(0)} }
        @keyframes rp-blob  {
          0%,100% { border-radius:60% 40% 30% 70%/60% 30% 70% 40%; transform:scale(1) translate(0,0); }
          33%      { border-radius:30% 60% 70% 40%/50% 60% 30% 60%; transform:scale(1.08) translate(8px,-8px); }
          66%      { border-radius:50% 60% 30% 60%/40% 30% 60% 50%; transform:scale(.95) translate(-6px,6px); }
        }
        @keyframes rp-grad  { 0%,100%{background-position:0% 50%} 50%{background-position:100% 50%} }
        @keyframes rp-spin  { from{transform:rotate(0)} to{transform:rotate(360deg)} }
        @keyframes rp-shine {
          0%   { transform:translateX(-100%) skewX(-15deg); }
          100% { transform:translateX(250%) skewX(-15deg); }
        }
        @keyframes rp-float { 0%,100%{transform:translateY(0)} 50%{transform:translateY(-8px)} }

        .rp .au  { animation: rp-up   .7s cubic-bezier(.22,1,.36,1) both; }
        .rp .al  { animation: rp-left .7s cubic-bezier(.22,1,.36,1) both; }

        .rp .d1  { animation-delay:.06s }
        .rp .d2  { animation-delay:.12s }
        .rp .d3  { animation-delay:.18s }
        .rp .d4  { animation-delay:.24s }
        .rp .d5  { animation-delay:.30s }
        .rp .d6  { animation-delay:.38s }
        .rp .d7  { animation-delay:.46s }

        .rp .blob {
          animation: rp-blob 10s ease-in-out infinite;
          position: absolute;
          filter: blur(60px);
        }
        .rp .blob-2 { animation-delay:-3.5s; }
        .rp .blob-3 { animation-delay:-7s; }

        .rp .grad-text {
          background: linear-gradient(135deg, #ef4444 0%, #f97316 40%, #ef4444 70%, #dc2626 100%);
          background-size: 300% 300%;
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
          animation: rp-grad 4s ease infinite;
        }

        .rp .btn-submit {
          background: linear-gradient(135deg, #ef4444, #dc2626, #b91c1c);
          background-size: 200% 200%;
          animation: rp-grad 3s ease infinite;
          transition: transform .2s, box-shadow .2s;
          position: relative;
          overflow: hidden;
        }
        .rp .btn-submit:hover {
          transform: translateY(-2px) scale(1.02);
          box-shadow: 0 16px 40px rgba(239,68,68,.45);
        }
        .rp .btn-submit:active { transform: scale(.97); }
        .rp .btn-submit::after {
          content:'';
          position:absolute;
          top:0; left:0;
          width:35%; height:100%;
          background: linear-gradient(90deg, transparent, rgba(255,255,255,.15), transparent);
          transform: translateX(-100%) skewX(-15deg);
        }
        .rp .btn-submit:hover::after { animation: rp-shine .5s ease forwards; }

        .rp .section-card {
          transition: border-color .3s;
        }

        .rp .feature-item {
          transition: transform .3s cubic-bezier(.22,1,.36,1);
        }
        .rp .feature-item:hover { transform: translateX(6px); }

        .rp .spin-ring  { animation: rp-spin 22s linear infinite; }
        .rp .spin-ring2 { animation: rp-spin 14s linear infinite reverse; }
        .rp .float-stat { animation: rp-float 3.5s ease-in-out infinite; }
        .rp .float-stat-2 { animation: rp-float 3.5s ease-in-out infinite; animation-delay:1.2s; }
        .rp .float-stat-3 { animation: rp-float 3.5s ease-in-out infinite; animation-delay:2.4s; }

        .rp .grid-bg-dark {
          background-image:
            linear-gradient(rgba(255,255,255,.025) 1px, transparent 1px),
            linear-gradient(90deg, rgba(255,255,255,.025) 1px, transparent 1px);
          background-size: 48px 48px;
        }
        .rp .grid-bg-light {
          background-image:
            linear-gradient(rgba(0,0,0,.04) 1px, transparent 1px),
            linear-gradient(90deg, rgba(0,0,0,.04) 1px, transparent 1px);
          background-size: 48px 48px;
        }

        .rp .pill {
          display: inline-flex;
          align-items: center;
          gap: 6px;
          padding: 4px 12px;
          border-radius: 999px;
          font-size: 11px;
          font-weight: 800;
          letter-spacing: .1em;
          text-transform: uppercase;
        }

        /* Input theming */
        .rp.dark-mode input, .rp.dark-mode textarea {
          background: rgba(255,255,255,.05) !important;
          border-color: rgba(255,255,255,.1) !important;
          color: white !important;
        }
        .rp.dark-mode input:focus, .rp.dark-mode textarea:focus {
          border-color: #ef4444 !important;
          box-shadow: 0 0 0 3px rgba(239,68,68,.15) !important;
        }
        .rp.dark-mode input::placeholder, .rp.dark-mode textarea::placeholder {
          color: rgba(255,255,255,.2) !important;
        }
        .rp.dark-mode label { color: rgba(255,255,255,.65) !important; }

        .rp.light-mode input, .rp.light-mode textarea {
          background: white !important;
          border-color: #e5e7eb !important;
          color: #111827 !important;
        }
        .rp.light-mode input:focus, .rp.light-mode textarea:focus {
          border-color: #ef4444 !important;
          box-shadow: 0 0 0 3px rgba(239,68,68,.1) !important;
        }
        .rp.light-mode label { color: #374151 !important; }
      `}</style>

      <Navbar />

      <div className={`rp ${d ? 'dark-mode' : 'light-mode'} min-h-screen flex pt-20 transition-colors duration-500 ${d ? 'bg-gray-950' : 'bg-gray-50'}`}>

        {/* ── LEFT PANEL ────────────────────────────────── */}
        <div className="hidden lg:flex lg:w-5/12 relative overflow-hidden flex-shrink-0">

          {/* BG */}
          <div className="absolute inset-0" style={{
            background: d
              ? 'linear-gradient(145deg, #030712 0%, #1c0000 45%, #030712 100%)'
              : 'linear-gradient(145deg, #fff1f2 0%, #ffe4e6 45%, #fff1f2 100%)',
          }}/>
          <div className={`absolute inset-0 ${d ? 'grid-bg-dark' : 'grid-bg-light'}`}/>

          {/* Blobs */}
          <div className="blob blob-1 w-72 h-72 top-10 left-6 opacity-30"
            style={{ background: d ? '#ef4444' : '#fca5a5' }}/>
          <div className="blob blob-2 w-60 h-60 top-52 right-4 opacity-20"
            style={{ background: d ? '#ffffff' : '#fed7aa' }}/>
          <div className="blob blob-3 w-56 h-56 bottom-16 left-28 opacity-25"
            style={{ background: d ? '#dc2626' : '#fca5a5' }}/>

          {/* Spinning rings */}
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-80 h-80 spin-ring opacity-10 pointer-events-none">
            <svg viewBox="0 0 280 280" fill="none" className="w-full h-full">
              <circle cx="140" cy="140" r="130" stroke="#ef4444" strokeWidth="1.5" strokeDasharray="10 7"/>
            </svg>
          </div>
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-56 h-56 spin-ring2 opacity-10 pointer-events-none">
            <svg viewBox="0 0 200 200" fill="none" className="w-full h-full">
              <circle cx="100" cy="100" r="92" stroke="#ef4444" strokeWidth="1" strokeDasharray="6 9"/>
            </svg>
          </div>

          {/* Content */}
          <div className="relative z-10 flex flex-col justify-center px-12 py-14">

            <div className="al d1 mb-3">
              <span className={`pill ${d ? 'bg-red-500/10 text-red-400 border border-red-500/20' : 'bg-red-50 text-red-600 border border-red-200'}`}>
                <span className="w-1.5 h-1.5 rounded-full bg-red-500 animate-pulse inline-block"/>
                Get Started
              </span>
            </div>

            <h1 className={`al d2 text-4xl xl:text-5xl font-black leading-tight mb-4 ${d ? 'text-white' : 'text-gray-900'}`}>
              Join<br/><span className="grad-text">eBallot Box</span>
            </h1>

            <p className={`al d3 text-sm mb-10 leading-relaxed max-w-xs ${d ? 'text-gray-400' : 'text-gray-500'}`}>
              Start hosting amazing contests, competitions, and awards with enterprise-grade security.
            </p>

            {/* Features */}
            <div className="space-y-4 mb-10">
              {[
                { emoji:'🔒', title:'Secure & Trusted', desc:'Bank-level security for all contests', delay:'d3' },
                { emoji:'⚡', title:'Fast Setup', desc:'Create and launch contests in minutes', delay:'d4' },
                { emoji:'📊', title:'Real-time Analytics', desc:'Track votes and engagement live', delay:'d5' },
              ].map((f, i) => (
                <div key={i} className={`feature-item al ${f.delay} flex items-center gap-3`}>
                  <div className={`w-10 h-10 rounded-xl flex items-center justify-center text-lg flex-shrink-0 border ${d ? 'bg-white/8 border-white/10' : 'bg-white border-gray-200 shadow-sm'}`}>
                    {f.emoji}
                  </div>
                  <div>
                    <p className={`text-sm font-bold ${d ? 'text-white' : 'text-gray-900'}`}>{f.title}</p>
                    <p className={`text-xs ${d ? 'text-gray-400' : 'text-gray-500'}`}>{f.desc}</p>
                  </div>
                </div>
              ))}
            </div>

            {/* Stats */}
            <div className={`al d6 grid grid-cols-3 divide-x rounded-2xl overflow-hidden border ${d ? 'bg-white/5 border-white/10 divide-white/10' : 'bg-white border-gray-200 divide-gray-200 shadow-md'}`}>
              {[
                { val:'500+', label:'Organizations', cls:'float-stat' },
                { val:'1M+', label:'Votes Cast', cls:'float-stat-2' },
                { val:'99.9%', label:'Uptime', cls:'float-stat-3' },
              ].map((s, i) => (
                <div key={i} className={`${s.cls} py-4 text-center`}>
                  <div className="text-xl font-black text-red-500 mb-0.5">{s.val}</div>
                  <div className={`text-[10px] font-bold uppercase tracking-wider ${d ? 'text-gray-400' : 'text-gray-500'}`}>{s.label}</div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* ── RIGHT PANEL ───────────────────────────────── */}
        <div className={`w-full lg:w-7/12 flex items-start justify-center p-6 sm:p-10 overflow-y-auto relative ${d ? 'bg-gray-950' : 'bg-white'}`}>

          {/* Top-right glow */}
          <div className="absolute top-0 right-0 w-72 h-72 rounded-full blur-3xl opacity-10 pointer-events-none"
            style={{ background: 'radial-gradient(circle, #ef4444, transparent)' }}/>

          <div className="w-full max-w-xl relative z-10 py-8">

            {/* Header */}
            <div className="au d1 text-center mb-8">
              <h2 className={`text-3xl font-black mb-1.5 ${d ? 'text-white' : 'text-gray-900'}`}>
                Create Account
              </h2>
              <p className={`text-sm ${d ? 'text-gray-400' : 'text-gray-500'}`}>
                Register to start hosting contests
              </p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-5">

              {/* ── Personal Info Card ── */}
              <div className={`au d2 section-card rounded-2xl border p-6 ${d
                ? 'bg-gray-900/80 border-white/8 hover:border-red-500/25'
                : 'bg-gray-50 border-gray-200 hover:border-red-200 shadow-sm'
              }`}>
                {/* Card header */}
                <div className="flex items-center gap-3 mb-5">
                  <div className="w-9 h-9 bg-gradient-to-br from-red-500 to-red-700 rounded-xl flex items-center justify-center shadow-md shadow-red-600/25 flex-shrink-0">
                    <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"/>
                    </svg>
                  </div>
                  <div>
                    <h3 className={`text-base font-black ${d ? 'text-white' : 'text-gray-900'}`}>Personal Information</h3>
                    <p className={`text-xs ${d ? 'text-gray-500' : 'text-gray-400'}`}>Your account details</p>
                  </div>
                </div>

                <div className="space-y-4">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <Input label="First Name" name="firstName" value={formData.firstName} onChange={handleChange} error={errors.firstName} required/>
                    <Input label="Last Name" name="lastName" value={formData.lastName} onChange={handleChange} error={errors.lastName} required/>
                  </div>
                  <Input label="Email Address" type="email" name="email" value={formData.email} onChange={handleChange} placeholder="john@example.com" error={errors.email} required/>
                  <Input label="Phone Number" type="tel" name="phone" value={formData.phone} onChange={handleChange} placeholder="+233241234567" error={errors.phone} required/>
                  <Input label="ID Number" name="idNumber" value={formData.idNumber} onChange={handleChange} placeholder="GHA-123456789" error={errors.idNumber} required/>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <Input label="Create Password" type="password" name="password" value={formData.password} onChange={handleChange} placeholder="••••••••" error={errors.password} required/>
                    <Input label="Confirm Password" type="password" name="confirmPassword" value={formData.confirmPassword} onChange={handleChange} placeholder="••••••••" error={errors.confirmPassword} required/>
                  </div>

                  {/* Password strength */}
                  {formData.password && (
                    <div className="space-y-2">
                      <div className="flex items-center gap-2">
                        <div className="flex gap-1.5 flex-1">
                          {[pwLen, pwCase, pwNum].map((met, i) => (
                            <div key={i} className="h-1.5 flex-1 rounded-full transition-all duration-400"
                              style={{ background: met ? strengthColor : d ? 'rgba(255,255,255,.1)' : '#e5e7eb' }}/>
                          ))}
                        </div>
                        <span className="text-xs font-bold" style={{ color: strengthColor }}>{strengthLabel}</span>
                      </div>
                      <p className={`text-xs ${d ? 'text-gray-500' : 'text-gray-400'}`}>
                        8+ chars · uppercase · lowercase · number
                      </p>
                    </div>
                  )}
                </div>
              </div>

              {/* ── Organization Info Card ── */}
              <div className={`au d3 section-card rounded-2xl border p-6 ${d
                ? 'bg-gray-900/80 border-white/8 hover:border-red-500/25'
                : 'bg-gray-50 border-gray-200 hover:border-red-200 shadow-sm'
              }`}>
                <div className="flex items-center gap-3 mb-5">
                  <div className="w-9 h-9 bg-gradient-to-br from-red-500 to-red-700 rounded-xl flex items-center justify-center shadow-md shadow-red-600/25 flex-shrink-0">
                    <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"/>
                    </svg>
                  </div>
                  <div>
                    <h3 className={`text-base font-black ${d ? 'text-white' : 'text-gray-900'}`}>Organization Info</h3>
                    <p className={`text-xs ${d ? 'text-gray-500' : 'text-gray-400'}`}>Your company or event details</p>
                  </div>
                </div>

                <div className="space-y-4">
                  <Input label="Organization Name" name="hostName" value={formData.hostName} onChange={handleChange} placeholder="Awesome Events GH" error={errors.hostName} required/>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <Input label="Organization Email" type="email" name="hostEmail" value={formData.hostEmail} onChange={handleChange} placeholder="events@awesome.com" error={errors.hostEmail} required/>
                    <Input label="Organization Phone" type="tel" name="hostPhone" value={formData.hostPhone} onChange={handleChange} placeholder="+233201234567" error={errors.hostPhone} required/>
                  </div>
                  <Input label="Organization Description" name="hostDescription" value={formData.hostDescription} onChange={handleChange} placeholder="We organize the best events in Ghana" error={errors.hostDescription} required/>
                </div>
              </div>

              {/* Terms notice */}
              <div className={`au d4 flex items-start gap-3 px-4 py-3.5 rounded-xl border ${d ? 'bg-red-500/5 border-red-500/20' : 'bg-red-50 border-red-100'}`}>
                <svg className="w-4 h-4 text-red-500 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/>
                </svg>
                <p className={`text-xs leading-relaxed ${d ? 'text-gray-400' : 'text-gray-500'}`}>
                  By creating an account, you agree to our{' '}
                  <Link to="/terms" className="text-red-500 hover:text-red-400 font-semibold">Terms</Link>
                  {' '}and{' '}
                  <Link to="/privacy" className="text-red-500 hover:text-red-400 font-semibold">Privacy Policy</Link>.
                </p>
              </div>

              {/* Submit */}
              <button
                type="submit"
                disabled={loading}
                className="au d5 btn-submit w-full text-white font-black text-base py-3.5 rounded-xl flex items-center justify-center gap-2 disabled:opacity-70 disabled:cursor-not-allowed"
              >
                {loading ? (
                  <>
                    <svg className="animate-spin h-5 w-5" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"/>
                    </svg>
                    Creating Account...
                  </>
                ) : (
                  <>
                    Create Account
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M13 7l5 5m0 0l-5 5m5-5H6"/>
                    </svg>
                  </>
                )}
              </button>

              {/* Sign in link */}
              <p className={`au d6 text-center text-sm ${d ? 'text-gray-400' : 'text-gray-500'}`}>
                Already have an account?{' '}
                <Link to="/login" className="text-red-500 hover:text-red-400 font-bold transition-colors relative group inline-block">
                  Sign in here
                  <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-red-500 group-hover:w-full transition-all duration-300"/>
                </Link>
              </p>

            </form>
          </div>
        </div>
      </div>
    </>
  );
};

export default RegisterPage;